import { useCallback, useEffect, useRef, useState } from "react";
import type { SignalEnvelope } from "../types";

type Handler = (message: SignalEnvelope) => void | Promise<void>;

export function useSignaling(token: string | null, handler: Handler) {
  const socket = useRef<WebSocket | null>(null);
  const handlerRef = useRef(handler);
  const retry = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  const ping = useRef<number | undefined>(undefined);
  const [connected, setConnected] = useState(false);

  handlerRef.current = handler;

  const connect = useCallback(() => {
    if (!token || socket.current?.readyState === WebSocket.OPEN) return;
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${protocol}//${location.host}/api/ws?token=${encodeURIComponent(token)}`);
    socket.current = ws;
    ws.onopen = () => {
      retry.current = 0;
      setConnected(true);
      ping.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping", payload: {} }));
      }, 20_000);
    };
    ws.onmessage = (event) => {
      try {
        void handlerRef.current(JSON.parse(event.data) as SignalEnvelope);
      } catch (error) {
        console.error("Invalid signaling message", error);
      }
    };
    ws.onclose = () => {
      setConnected(false);
      if (ping.current) clearInterval(ping.current);
      const delay = Math.min(30_000, 750 * 2 ** retry.current) + Math.random() * 500;
      retry.current += 1;
      timer.current = window.setTimeout(connect, delay);
    };
  }, [token]);

  useEffect(() => {
    connect();
    const online = () => connect();
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("online", online);
      if (timer.current) clearTimeout(timer.current);
      if (ping.current) clearInterval(ping.current);
      socket.current?.close();
      socket.current = null;
    };
  }, [connect]);

  const send = useCallback((message: SignalEnvelope) => {
    if (socket.current?.readyState !== WebSocket.OPEN) throw new Error("Signaling is offline");
    socket.current.send(JSON.stringify(message));
  }, []);

  return { connected, send, reconnect: connect };
}
